/**
 * Products & Pricing — Stripe products and enterprise packages.
 */

import React, { useMemo, useState } from 'react';
import { Alert, Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { AppIconButton, AppListPage } from '../design-system';
import {
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import ProviderStripeProductCreator from './ProviderStripeProductCreator';
import ProviderSubscriptionTable from './ProviderSubscriptionTable';
import EnterprisePackagesTable from './EnterprisePackagesTable';
import EnterprisePackageDialog from '../provider/EnterprisePackageDialog';
import type { EnterprisePackageData } from '@cnapse/shared/schemas.js';

type PricingTab = 'stripe' | 'enterprise';

const SubscriptionPlansPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<PricingTab>('stripe');
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<EnterprisePackageData | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleCreatorSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handlePackageSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton
        onClick={() => setRefreshTrigger((prev) => prev + 1)}
        aria-label="Refresh"
      >
        <RefreshIcon />
      </AppIconButton>
    ),
    [],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  return (
    <AppListPage>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <ToggleButtonGroup
          exclusive
          value={activeTab}
          onChange={(_, value: PricingTab | null) => {
            if (value) {
              setActiveTab(value);
            }
          }}
          size="small"
        >
          <ToggleButton value="stripe">Stripe</ToggleButton>
          <ToggleButton value="enterprise">Enterprise</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {activeTab === 'stripe' ? (
        <>
          <Alert severity="info" icon={<InfoIcon />} sx={{ flexShrink: 0 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Stripe products &amp; pricing
            </Typography>
            <Typography variant="body2" component="div">
              <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                <li>Create products with base monthly price and metered overages</li>
                <li>Add metadata for base quotas (base_users, base_wafers, etc.)</li>
                <li>Assign to tenants via Tenant Subscriptions → Stripe</li>
              </Box>
            </Typography>
          </Alert>

          <ProviderSubscriptionTable
            refreshTrigger={refreshTrigger}
            onAdd={() => setCreatorOpen(true)}
            addLabel="Create product & pricing"
          />

          <ProviderStripeProductCreator
            open={creatorOpen}
            onClose={() => setCreatorOpen(false)}
            onSuccess={handleCreatorSuccess}
          />
        </>
      ) : (
        <>
          <Alert severity="info" icon={<InfoIcon />} sx={{ flexShrink: 0 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Enterprise packages
            </Typography>
            <Typography variant="body2">
              Define packages for pilots, annual contracts, and mid-contract amendments.
              Pricing lives in Zoho; assign each package once on Tenant Subscriptions.
            </Typography>
          </Alert>

          <EnterprisePackagesTable
            refreshTrigger={refreshTrigger}
            onAdd={() => {
              setEditingPackage(null);
              setPackageDialogOpen(true);
            }}
            addLabel="Create enterprise package"
            onEdit={(pkg) => {
              setEditingPackage(pkg);
              setPackageDialogOpen(true);
            }}
          />

          <EnterprisePackageDialog
            open={packageDialogOpen}
            onClose={() => {
              setPackageDialogOpen(false);
              setEditingPackage(null);
            }}
            onSuccess={handlePackageSuccess}
            existingPackage={editingPackage}
          />
        </>
      )}
    </AppListPage>
  );
};

export default SubscriptionPlansPage;
